import React, { useEffect, useState, useRef } from "react";
import {
  Users,
  LogOut,
  ShieldCheck,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ChevronRight,
  ArrowLeft,
  FileCheck,
  Globe,
  Briefcase,
  DollarSign,
  Phone,
  MapPin,
  CreditCard,
  CalendarDays,
  TrendingUp,
  Search,
  X,
  LayoutDashboard,
  AlertTriangle,
  ExternalLink,
  Truck,
  Star,
  Wallet,
  Ban,
  RotateCcw,
  Mail,
  MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import type { User, KYCRequest } from "./types";
import API_BASE_URL from "../../ScrapFlow/config/config.server";
import supportService from "../../services/supportService";
import AdminDriverMap, { LiveDriverPoint } from "./AdminDriverMap";
import { adminFetch } from "./adminApi";
import { useAuth } from "../../ScrapFlow/context/AuthContext";
import AdminBookings from "./AdminBookings";
import AdminDisputes from "./AdminDisputes";
import AdminPayments from "./AdminPayments";
import AdminSupportTickets from "./AdminSupportTickets";
import AdminAuditLog from "./AdminAuditLog";

const KYC_API_PATH = `${API_BASE_URL}/api/admin/marketplace-verification/requests`;
const DRIVERS_API_PATH = `${API_BASE_URL}/api/admin/drivers`;
const SOS_API_PATH = `${API_BASE_URL}/api/admin/sos-alerts`;
const DRIVER_APP_URL = (import.meta as any).env?.VITE_DRIVER_APP_URL || "https://app.afdani.com";
// Ops dashboards for this kind of thing are almost always polling underneath
// even when they look "live" — a genuine socket-pushed admin feed would be a
// much bigger lift (this admin panel has no socket client at all today) for
// marginal gain over a tight poll. 8s matches the driver app's own location
// ping cadence, so the map is never more stale than the source data anyway.
const LIVE_POLL_MS = 8000;

const normalizeDocumentImageUrl = (src?: string) => {
  if (!src) return "";
  const trimmed = String(src).trim();
  if (trimmed === "") return "";
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
  return `${API_BASE_URL}${trimmed.startsWith("/") ? "" : "/"}${trimmed}`;
};

interface Props {
  onLogout: () => void;
  initialUsers?: User[];
  initialKycRequests?: KYCRequest[];
}

type MainView =
  | "dashboard"
  | "kyc-queue"
  | "kyc-detail"
  | "users-list"
  | "problem-reports"
  | "drivers-list"
  | "driver-detail"
  | "bookings"
  | "disputes"
  | "payments"
  | "support-tickets"
  | "audit-log";

type DriverKycStatus = "not_submitted" | "pending" | "verified" | "rejected";

interface DriverSummary {
  driverId: number;
  name: string;
  phone: string;
  email?: string;
  vehicleType?: string;
  vehicleNumber?: string;
  vehicleModel?: string;
  vehicleColor?: string;
  kycStatus: DriverKycStatus;
  isOnline: boolean;
  isActive: boolean;
  rating: number | null;
  totalTrips: number;
  acceptedTrips: number;
  declinedTrips: number;
  cancelledTrips: number;
  walletBalance: number;
  createdAt: string;
  currentLat?: number | null;
  currentLng?: number | null;
  lastLocationAt?: string | null;
  activeBookingId?: number | null;
  activeBookingStatus?: string | null;
}

interface SosAlert {
  alertId: number;
  bookingId: number;
  bookingCode?: string;
  driverId: number;
  driverName: string;
  driverPhone?: string;
  lat: number | null;
  lng: number | null;
  notes?: string;
  status: "open" | "resolved";
  pickupAddress?: string;
  dropoffAddress?: string;
  createdAt: string;
  resolvedAt?: string | null;
}

interface DriverKycDocs {
  drivingLicenseImage?: string;
  aadhaarImage?: string;
  panImage?: string;
  rcImage?: string;
  selfieImage?: string;
  insuranceImage?: string;
  permitImage?: string;
  profilePhotoImage?: string;
  status: DriverKycStatus;
  notes?: string;
  submittedAt?: string;
  reviewedAt?: string;
}

interface DriverDetail extends DriverSummary {
  address?: string;
  city?: string;
  pincode?: string;
  vehicleCapacityKg?: number | null;
}

interface ProblemReport {
  ReportID?: number;
  OpID?: number;
  Category?: string;
  Description?: string;
  Status?: string;
  CreatedAt?: string;
  UpdatedAt?: string;
  UserName?: string;
  ContactNo?: string;
}

const kycStatusBadge = (status: KYCRequest["status"]) => {
  const safeStatus = (status || "pending") as "pending" | "verified" | "rejected";
  const classMap: Record<string, string> = {
    pending: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    verified: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    rejected: "bg-red-500/10 text-red-400 border-red-500/20",
  };
  const Icon: any = { pending: Clock, verified: CheckCircle2, rejected: XCircle }[safeStatus] ?? Clock;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border capitalize ${classMap[safeStatus]}`}>
      <Icon size={9} />
      {safeStatus}
    </span>
  );
};

// Widened to plain string (not just User["kycStatus"]) so the same badge
// serves DriverSummary/DriverKycDocs too — the not_submitted/pending/
// verified/rejected enum is identical for both.
const userKycBadge = (status: User["kycStatus"] | DriverKycStatus | undefined) => {
  const safeStatus = (status || "not_submitted") as "not_submitted" | "pending" | "verified" | "rejected";
  const map: Record<string, string> = {
    not_submitted: "bg-[#1a2540] text-[#6b7fa3] border-[#1e2d4a]",
    pending: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    verified: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    rejected: "bg-red-500/10 text-red-400 border-red-500/20",
  };
  const labels: any = { not_submitted: "Not Submitted", pending: "Pending", verified: "Verified", rejected: "Rejected" };
  const Icon: any = { not_submitted: AlertCircle, pending: Clock, verified: CheckCircle2, rejected: XCircle }[safeStatus] ?? AlertCircle;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${map[safeStatus]}`}>
      <Icon size={9} />
      {labels[safeStatus]}
    </span>
  );
};

const riskBadge = (risk: KYCRequest["riskLevel"]) => {
  const map: Record<string, string> = {
    Low: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    Medium: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    High: "bg-red-500/10 text-red-400 border-red-500/20",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${map[risk as string]}`}>
      {risk === "High" && <AlertTriangle size={9} />}
      {risk} Risk
    </span>
  );
};

// ─── Global search (copied/adapted) ────────────────────────────────────────────
function GlobalSearch({ users, onImpersonate, onViewUser }: { users: User[]; onImpersonate: (u: User) => void; onViewUser: () => void; }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const mobileInputRef = useRef<HTMLInputElement>(null);

  const results = query.trim().length > 0
    ? users.filter((u) =>
        u.name.toLowerCase().includes(query.toLowerCase()) ||
        u.email.toLowerCase().includes(query.toLowerCase()) ||
        (u.role || "").toLowerCase().includes(query.toLowerCase()) ||
        (u.country || "").toLowerCase().includes(query.toLowerCase())
      ).slice(0, 6)
    : [];

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setOpen(false); setMobileOpen(false); setQuery(""); }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (mobileOpen) setTimeout(() => mobileInputRef.current?.focus(), 80);
  }, [mobileOpen]);

  const handleSelect = (user: User) => {
    setQuery("");
    setOpen(false);
    setMobileOpen(false);
    onImpersonate(user);
  };

  const ResultList = ({ inMobile }: { inMobile?: boolean }) => (
    <div className={`${inMobile ? "flex-1 overflow-y-auto" : "absolute top-full left-0 right-0 mt-1.5 rounded-2xl border border-white/[0.08] shadow-2xl shadow-black/60 overflow-hidden z-50"} bg-[#0c1428]`}>
      {results.length === 0 && query.trim().length > 0 && (
        <div className="px-4 py-8 text-center">
          <Search size={22} className="text-[#2a3a52] mx-auto mb-2" />
          <p className="text-[#6b7fa3] text-sm">No users found for "{query}"</p>
        </div>
      )}
      {results.length === 0 && query.trim().length === 0 && inMobile && (
        <div className="px-4 py-10 text-center">
          <Search size={26} className="text-[#2a3a52] mx-auto mb-2" />
          <p className="text-[#6b7fa3] text-sm">Search by name, email, role or country</p>
        </div>
      )}
      {results.map((user, i) => (
        <button key={user.id} onClick={() => handleSelect(user)} className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-[#0f1e3d] active:bg-[#0f1e3d] transition-colors text-left ${i < results.length - 1 ? "border-b border-white/[0.04]" : ""}`}>
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0" style={{ background: user.avatarColor + "33", border: `1px solid ${user.avatarColor}40`, color: user.avatarColor }}>{user.initials}</div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium truncate">{user.name}</p>
            <p className="text-[#6b7fa3] text-xs truncate">{user.email}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {userKycBadge(user.kycStatus)}
            <div className={`w-1.5 h-1.5 rounded-full ${user.sessionActive ? "bg-emerald-400" : "bg-[#2a3a52]"}`} />
          </div>
        </button>
      ))}
      {results.length > 0 && (
        <div className="px-4 py-2 border-t border-white/[0.05]">
          <button onClick={() => { setQuery(""); setOpen(false); setMobileOpen(false); onViewUser(); }} className="text-sm text-[#00c9a7] hover:underline">View all users →</button>
        </div>
      )}
    </div>
  );

  return (
    <>
      <div ref={wrapRef} className="relative hidden sm:block w-56 lg:w-72">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#3d4f6a] pointer-events-none" />
        <input ref={inputRef} type="text" value={query} onChange={(e) => { setQuery(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} placeholder="Search users…" className="w-full bg-[#0c1428] border border-white/[0.08] hover:border-white/[0.12] focus:border-[#00c9a7]/40 text-white rounded-xl pl-8 pr-8 py-2 text-sm placeholder:text-[#3d4f6a] focus:outline-none focus:ring-2 focus:ring-[#00c9a7]/10 transition-all" />
        {query && (<button onClick={() => { setQuery(""); setOpen(false); inputRef.current?.focus(); }} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#3d4f6a] hover:text-white transition-colors p-0.5"><X size={12} /></button>)}
        {open && (results.length > 0 || query.trim().length > 0) && <ResultList />}
      </div>

      <button onClick={() => setMobileOpen(true)} className="sm:hidden flex items-center justify-center w-9 h-9 bg-[#0c1428] border border-white/[0.08] rounded-xl text-[#6b7fa3] hover:text-white transition-colors"><Search size={15} /></button>

      {mobileOpen && (
        <div className="sm:hidden fixed inset-0 z-50 bg-[#060b18] flex flex-col" style={{ paddingTop: "env(safe-area-inset-top)" }}>
          <div className="flex items-center gap-3 px-4 py-3 border-b border-white/[0.07]">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#3d4f6a] pointer-events-none" />
              <input ref={mobileInputRef} type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search users by name, email, role…" className="w-full bg-[#0c1428] border border-white/[0.08] text-white rounded-xl pl-9 pr-9 py-2.5 text-sm placeholder:text-[#3d4f6a] focus:outline-none focus:border-[#00c9a7]/30 transition-all" />
              {query && (<button onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#3d4f6a] hover:text-white transition-colors"><X size={14} /></button>)}
            </div>
            <button onClick={() => { setMobileOpen(false); setQuery(""); }} className="text-[#6b7fa3] hover:text-white text-sm font-medium min-h-[40px] px-1 transition-colors shrink-0">Cancel</button>
          </div>
          <ResultList inMobile />
        </div>
      )}
    </>
  );
}

function StatCard({ label, value, sub, icon: Icon, color }: { label: string; value: string | number; sub?: string; icon: React.ElementType; color: string; }) {
  return (
    <div className="bg-[#0c1428] border border-white/[0.06] rounded-2xl p-3.5 sm:p-5">
      <div className="w-8 h-8 rounded-xl flex items-center justify-center mb-2" style={{ background: `${color}18` }}>
        <Icon size={14} style={{ color }} />
      </div>
      <p className="text-lg sm:text-xl font-semibold text-white tracking-tight">{value}</p>
      <p className="text-[#6b7fa3] text-[11px] mt-0.5">{label}</p>
      {sub && <p className="text-[#3d4f6a] text-[11px] mt-1 hidden sm:block">{sub}</p>}
    </div>
  );
}

function MobileBottomNav({ mainView, setMainView, pendingKYC, pendingDriverKYC, openSOSCount, onLogout }: { mainView: MainView; setMainView: (view: MainView) => void; pendingKYC: number; pendingDriverKYC: number; openSOSCount: number; onLogout: () => void; }) {
  // Six items now (was four + a standalone Logout button that used to wrap
  // onto its own row in a 4-col grid) — grid-cols-6 with tighter icon/label
  // sizing keeps this one compact row instead of growing to two, which is
  // what "more compact on small devices" actually calls for here.
  // An open SOS alert takes priority over the (less urgent) pending-KYC
  // count on the same Drivers tab badge, rather than showing both at once.
  const navItems = [
    { view: "dashboard" as MainView, label: "Home", icon: LayoutDashboard },
    { view: "users-list" as MainView, label: "Users", icon: Users },
    {
      view: "drivers-list" as MainView,
      label: "Drivers",
      icon: Truck,
      badge: openSOSCount ? String(openSOSCount) : pendingDriverKYC ? String(pendingDriverKYC) : undefined,
      urgent: openSOSCount > 0,
    },
    { view: "kyc-queue" as MainView, label: "KYC", icon: FileCheck, badge: pendingKYC ? String(pendingKYC) : undefined },
    { view: "problem-reports" as MainView, label: "Reports", icon: AlertTriangle },
  ];

  return (
    <div className="sm:hidden fixed bottom-0 left-0 right-0 z-30 border-t border-white/[0.08] bg-[#060b18]/95 backdrop-blur-xl" style={{ paddingBottom: "max(10px, env(safe-area-inset-bottom))" }}>
      <div className="max-w-7xl mx-auto px-2 py-1.5 grid grid-cols-6 gap-1">
        {navItems.map((item) => {
          const active = mainView === item.view || (item.view === "drivers-list" && mainView === "driver-detail");
          const Icon = item.icon;
          return (
            <button key={item.view} onClick={() => setMainView(item.view)} className={`flex flex-col items-center justify-center gap-0.5 rounded-xl py-1.5 transition ${active ? "bg-[#0c1428] text-white" : "bg-[#0a1020] text-[#6b7fa3] hover:text-white"}`}>
              <div className="relative">
                <Icon size={15} />
                {item.badge && (
                  <span className={`absolute -top-1.5 -right-2 min-w-[14px] rounded-full text-[9px] font-semibold px-1 leading-[14px] ${item.urgent ? "bg-red-500 text-white" : "bg-amber-500 text-[#060b18]"}`}>
                    {item.badge}
                  </span>
                )}
              </div>
              <span className="text-[8.5px] uppercase tracking-[0.04em] leading-none">{item.label}</span>
            </button>
          );
        })}
        <button onClick={onLogout} className="flex flex-col items-center justify-center gap-0.5 rounded-xl py-1.5 bg-[#0a1020] text-[#6b7fa3] hover:text-white transition">
          <LogOut size={15} />
          <span className="text-[8.5px] uppercase tracking-[0.04em] leading-none">Logout</span>
        </button>
      </div>
    </div>
  );
}

function KYCDetailView({ request, onBack, onReview }: { request: KYCRequest; onBack: () => void; onReview: (id: string, action: "verify" | "reject" | "pending") => void; }) {
  const infoGroups = [
    {
      title: "Personal Information",
      fields: [
        { label: "Full Legal Name", value: request.fullLegalName, icon: Users },
        { label: "Date of Birth", value: request.dateOfBirth, icon: CalendarDays },
        { label: "Nationality", value: request.nationality, icon: Globe },
        { label: "Phone Number", value: request.phone, icon: Phone },
      ],
    },
    {
      title: "Identity Document",
      fields: [
        { label: "Document Type", value: request.idType, icon: CreditCard },
        { label: "ID Number", value: request.idNumber, icon: CreditCard },
        { label: "Expiry Date", value: request.idExpiry, icon: CalendarDays },
      ],
    },
    {
      title: "Address",
      fields: [
        { label: "Street Address", value: request.address, icon: MapPin },
        { label: "City", value: request.city, icon: MapPin },
        { label: "Country", value: request.country, icon: Globe },
        { label: "Postal Code", value: request.postalCode, icon: MapPin },
      ],
    },
    {
      title: "Financial Profile",
      fields: [
        { label: "Occupation", value: request.occupation, icon: Briefcase },
        { label: "Source of Funds", value: request.sourceOfFunds, icon: DollarSign },
        { label: "Annual Income", value: request.annualIncome, icon: TrendingUp },
      ],
    },
  ];

  const documentImages = [
    { label: "PAN Front", src: normalizeDocumentImageUrl(request.panFrontImage) },
    { label: "PAN Back", src: normalizeDocumentImageUrl(request.panBackImage) },
    { label: "Aadhaar Front", src: normalizeDocumentImageUrl(request.aadhaarFrontImage) },
    { label: "Selfie", src: normalizeDocumentImageUrl(request.selfieImage) },
  ].filter((i) => Boolean(i.src));

  return (
    <div className="min-h-screen bg-[#060b18] font-['Inter',sans-serif] pb-32 sm:pb-0">
      <div className="fixed inset-0 opacity-[0.025]" style={{ backgroundImage: "linear-gradient(#00c9a7 1px, transparent 1px), linear-gradient(90deg, #00c9a7 1px, transparent 1px)", backgroundSize: "60px 60px" }} />

      <header className="sticky top-0 z-20 bg-[#060b18]/90 border-b border-white/[0.06]" style={{ backdropFilter: "blur(12px)" }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
          <button onClick={onBack} className="flex items-center gap-1.5 text-[#6b7fa3] hover:text-white text-sm transition-colors min-h-[40px]">
            <ArrowLeft size={15} />
            <span className="hidden sm:inline">Back to KYC Queue</span>
            <span className="sm:hidden">Queue</span>
          </button>
          <span className="text-white/10">·</span>
          <p className="text-white text-sm font-medium truncate">{request.userName}</p>
        </div>
      </header>

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-5 sm:py-8 space-y-4 sm:space-y-5">
        <div className="bg-[#0c1428] border border-white/[0.07] rounded-2xl p-4 sm:p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-amber-500/15 border border-amber-500/25 flex items-center justify-center shrink-0">
              <span className="text-amber-400 text-base sm:text-xl font-bold">{request.userName.split(" ").map((n) => n[0]).join("")}</span>
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-white text-base sm:text-xl font-semibold truncate">{request.userName}</h1>
              <p className="text-[#6b7fa3] text-xs sm:text-sm truncate">{request.email}</p>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {kycStatusBadge(request.status)}
                {riskBadge(request.riskLevel)}
              </div>
              <p className="text-[#3d4f6a] text-[11px] mt-1.5 font-mono">{request.id} · Submitted {new Date(request.submittedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</p>
            </div>
          </div>
        </div>

        {documentImages.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {documentImages.map((doc) => (
              <div key={doc.label} className="bg-[#0c1428] border border-white/[0.06] rounded-2xl overflow-hidden">
                <div className="px-4 py-3 border-b border-white/[0.05] flex items-center justify-between gap-2">
                  <p className="text-[#a0aec0] text-[10px] font-medium tracking-widest uppercase">{doc.label}</p>
                  <a
                    href={doc.src}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/[0.08] text-[#6b7fa3] hover:border-[#00c9a7] hover:text-[#00c9a7] transition-colors"
                    aria-label={`Open ${doc.label} full image`}
                  >
                    <ExternalLink size={14} />
                  </a>
                </div>
                <div className="p-3">
                  <a href={doc.src} target="_blank" rel="noreferrer" className="block overflow-hidden rounded-xl">
                    <img
                      src={doc.src}
                      alt={doc.label}
                      loading="lazy"
                      className="w-full h-32 sm:h-40 object-cover rounded-xl border border-white/[0.05]"
                      onError={(event) => { event.currentTarget.style.display = 'none'; }}
                    />
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {infoGroups.map(({ title, fields }) => (
            <div key={title} className="bg-[#0c1428] border border-white/[0.06] rounded-2xl overflow-hidden">
              <div className="px-4 sm:px-5 py-3 border-b border-white/[0.05]">
                <h2 className="text-[#a0aec0] text-[10px] font-medium tracking-widest uppercase">{title}</h2>
              </div>
              <div className="p-3 sm:p-4 space-y-2">
                {fields.map(({ label, value, icon: Icon }) => (
                  <div key={label} className="flex items-start gap-3 bg-[#0a1020] rounded-xl p-2.5">
                    <div className="w-6 h-6 rounded-lg bg-[#00c9a7]/10 flex items-center justify-center shrink-0 mt-0.5">
                      <Icon size={11} className="text-[#00c9a7]" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[#6b7fa3] text-[11px]">{label}</p>
                      <p className="text-white text-sm font-medium mt-0.5 break-words">{value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {request.status === "pending" && (
          <div className="hidden sm:flex bg-[#0c1428] border border-white/[0.06] rounded-2xl p-5 items-center justify-between gap-4">
            <div>
              <p className="text-white font-medium text-sm">Ready to make a decision?</p>
              <p className="text-[#6b7fa3] text-xs mt-0.5">This will update the user's KYC status and marketplace access.</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => onReview(request.id, "reject")} className="px-6 py-2.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400 rounded-xl text-sm font-medium transition-all">Reject</button>
              <button onClick={() => onReview(request.id, "verify")} className="px-6 py-2.5 bg-[#00c9a7] hover:bg-[#00b396] text-[#060b18] rounded-xl text-sm font-semibold transition-all">Verify & Approve</button>
            </div>
          </div>
        )}

        {request.status === "verified" && (
          <div className="hidden sm:flex bg-[#0c1428] border border-white/[0.06] rounded-2xl p-5 items-center justify-between gap-4">
            <div>
              <p className="text-white font-medium text-sm">Reset verification status</p>
              <p className="text-[#6b7fa3] text-xs mt-0.5">Verified users can be reopened for review or rejected again.</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => onReview(request.id, "pending")} className="px-6 py-2.5 bg-[#1f2937] hover:bg-[#111827] border border-white/[0.08] text-[#9ca3af] rounded-xl text-sm font-medium transition-all">Reset to Pending</button>
              <button onClick={() => onReview(request.id, "reject")} className="px-6 py-2.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400 rounded-xl text-sm font-medium transition-all">Reject</button>
            </div>
          </div>
        )}

        {request.status === "rejected" && (
          <div className="hidden sm:flex bg-[#0c1428] border border-white/[0.06] rounded-2xl p-5 items-center justify-between gap-4">
            <div>
              <p className="text-white font-medium text-sm">Reopen rejected request</p>
              <p className="text-[#6b7fa3] text-xs mt-0.5">Rejected requests can also be reopened for review or approved directly.</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => onReview(request.id, "pending")} className="px-6 py-2.5 bg-[#1f2937] hover:bg-[#111827] border border-white/[0.08] text-[#9ca3af] rounded-xl text-sm font-medium transition-all">Reopen to Pending</button>
              <button onClick={() => onReview(request.id, "verify")} className="px-6 py-2.5 bg-[#00c9a7] hover:bg-[#00b396] text-[#060b18] rounded-xl text-sm font-semibold transition-all">Verify & Approve</button>
            </div>
          </div>
        )}

        {request.status === "pending" && (
          <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-[#0c1428] border-t border-white/[0.08] px-4 py-3 flex gap-3" style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom))" }}>
            <button onClick={() => onReview(request.id, "reject")} className="flex-1 min-h-[48px] bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl text-sm font-medium transition-all">Reject</button>
            <button onClick={() => onReview(request.id, "verify")} className="flex-1 min-h-[48px] bg-[#00c9a7] text-[#060b18] rounded-xl text-sm font-semibold transition-all">Verify & Approve</button>
          </div>
        )}

        {request.status === "verified" && (
          <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-[#0c1428] border-t border-white/[0.08] px-4 py-3 flex gap-3" style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom))" }}>
            <button onClick={() => onReview(request.id, "pending")} className="flex-1 min-h-[48px] bg-[#1f2937] border border-white/[0.08] text-[#9ca3af] rounded-xl text-sm font-medium transition-all">Reset to Pending</button>
            <button onClick={() => onReview(request.id, "reject")} className="flex-1 min-h-[48px] bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl text-sm font-medium transition-all">Reject</button>
          </div>
        )}

        {request.status === "rejected" && (
          <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-[#0c1428] border-t border-white/[0.08] px-4 py-3 flex gap-3" style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom))" }}>
            <button onClick={() => onReview(request.id, "pending")} className="flex-1 min-h-[48px] bg-[#1f2937] border border-white/[0.08] text-[#9ca3af] rounded-xl text-sm font-medium transition-all">Reopen to Pending</button>
            <button onClick={() => onReview(request.id, "verify")} className="flex-1 min-h-[48px] bg-[#00c9a7] text-[#060b18] rounded-xl text-sm font-semibold transition-all">Verify & Approve</button>
          </div>
        )}

      </div>
    </div>
  );
}

function KYCQueueView({ kycRequests, onBack, onSelectRequest }: { kycRequests: KYCRequest[]; onBack: () => void; onSelectRequest: (req: KYCRequest) => void; }) {
  const [filter, setFilter] = useState<"all" | KYCRequest["status"]>("all");
  const [search, setSearch] = useState("");

  const filtered = kycRequests.filter((r) => {
    const matchFilter = filter === "all" || r.status === filter;
    const matchSearch = r.userName.toLowerCase().includes(search.toLowerCase()) || r.email.toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  const pending = kycRequests.filter((r) => r.status === "pending").length;
  const verified = kycRequests.filter((r) => r.status === "verified").length;
  const rejected = kycRequests.filter((r) => r.status === "rejected").length;

  return (
    <div className="min-h-screen bg-[#060b18] font-['Inter',sans-serif] pb-20 sm:pb-0">
      <div className="fixed inset-0 opacity-[0.025]" style={{ backgroundImage: "linear-gradient(#00c9a7 1px, transparent 1px), linear-gradient(90deg, #00c9a7 1px, transparent 1px)", backgroundSize: "60px 60px" }} />

      <header className="sticky top-0 z-20 bg-[#060b18]/90 border-b border-white/[0.06]" style={{ backdropFilter: "blur(12px)" }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-12 sm:h-14 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={onBack} className="flex items-center gap-1.5 text-[#6b7fa3] hover:text-white text-sm transition-colors min-h-[40px] shrink-0">
              <ArrowLeft size={15} />
              <span className="hidden sm:inline">Dashboard</span>
            </button>
            <span className="text-white/10 hidden sm:inline">·</span>
            <div className="hidden sm:flex items-center gap-2">
              <FileCheck size={14} className="text-[#00c9a7]" />
              <span className="text-white font-medium text-sm">KYC Verification Queue</span>
            </div>
            <span className="sm:hidden text-white font-medium text-sm">KYC Queue</span>
          </div>
          {pending > 0 && <span className="bg-amber-500 text-[#060b18] text-[11px] font-bold px-2 py-0.5 rounded-full">{pending} pending</span>}
        </div>
      </header>

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-5 sm:py-8 space-y-4 sm:space-y-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div className="bg-[#0c1428] border border-amber-500/10 rounded-2xl p-3">
            <div className="flex items-center gap-1 mb-1">
              <Clock size={12} className="text-amber-400" />
              <span className="text-[#6b7fa3] text-[11px]">Pending</span>
            </div>
            <p className="text-xl sm:text-2xl font-semibold text-amber-400">{pending}</p>
          </div>
          <div className="bg-[#0c1428] border border-emerald-500/10 rounded-2xl p-3">
            <div className="flex items-center gap-1 mb-1">
              <CheckCircle2 size={12} className="text-emerald-400" />
              <span className="text-[#6b7fa3] text-[11px]">Approved</span>
            </div>
            <p className="text-xl sm:text-2xl font-semibold text-emerald-400">{verified}</p>
          </div>
          <div className="bg-[#0c1428] border border-red-500/10 rounded-2xl p-3">
            <div className="flex items-center gap-1 mb-1">
              <XCircle size={12} className="text-red-400" />
              <span className="text-[#6b7fa3] text-[11px]">Rejected</span>
            </div>
            <p className="text-xl sm:text-2xl font-semibold text-red-400">{rejected}</p>
          </div>
        </div>

        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#3d4f6a]" />
          <input type="text" placeholder="Search by name or email…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-full bg-[#0c1428] border border-white/[0.07] text-white rounded-xl pl-9 pr-4 py-2.5 text-sm placeholder:text-[#3d4f6a] focus:outline-none focus:border-[#00c9a7]/30 transition-all" />
        </div>

        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none -mx-4 px-4 sm:mx-0 sm:px-0">
          {(["all", "pending", "verified", "rejected"] as const).map((f) => (
            <button key={f} onClick={() => setFilter(f as any)} className={`px-3.5 py-2 rounded-xl text-xs font-medium transition-all capitalize whitespace-nowrap min-h-[36px] ${filter === f ? "bg-[#00c9a7] text-[#060b18]" : "bg-[#0c1428] border border-white/[0.07] text-[#6b7fa3] hover:text-white"}`}>
              {f}
            </button>
          ))}
        </div>

        <div className="space-y-2.5 sm:space-y-3">
          {filtered.length === 0 && <div className="text-center py-14 text-[#6b7fa3] text-sm">No KYC requests match your filter</div>}
          {filtered.map((req) => (
            <button key={req.id} onClick={() => onSelectRequest(req)} className="w-full bg-[#0c1428] active:bg-[#0f1e3d] hover:bg-[#0f1e3d] border border-white/[0.06] hover:border-[#00c9a7]/20 rounded-2xl p-4 sm:p-5 text-left transition-all group">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className={`w-10 h-10 sm:w-11 sm:h-11 rounded-2xl flex items-center justify-center shrink-0 ${req.status === "pending" ? "bg-amber-500/15 border border-amber-500/25" : "bg-white/5 border border-white/[0.07]"}`}>
                  <span className={`text-sm font-bold ${req.status === "pending" ? "text-amber-400" : "text-[#6b7fa3]"}`}>{req.userName.split(" ").map((n) => n[0]).join("")}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
                    <p className="text-white text-sm font-medium truncate">{req.userName}</p>
                    <div className="hidden sm:flex items-center gap-2">{kycStatusBadge(req.status)}{riskBadge(req.riskLevel)}</div>
                  </div>
                  <p className="text-[#6b7fa3] text-xs truncate">{req.email}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className={`w-2 h-2 rounded-full ${req.status === "verified" ? "bg-emerald-400" : req.status === "pending" ? "bg-amber-400" : "bg-red-400"}`} />
                  <ChevronRight size={14} className="text-[#3d4f6a]" />
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function UsersListView({ users, onBack, onImpersonate }: { users: User[]; onBack: () => void; onImpersonate: (user: User) => void; }) {
  const [search, setSearch] = useState("");

  const filtered = users.filter((u) =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase()) ||
    (u.role || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#060b18] font-['Inter',sans-serif] pb-20 sm:pb-0">
      <div className="fixed inset-0 opacity-[0.025]" style={{ backgroundImage: "linear-gradient(#00c9a7 1px, transparent 1px), linear-gradient(90deg, #00c9a7 1px, transparent 1px)", backgroundSize: "60px 60px" }} />

      <header className="sticky top-0 z-20 bg-[#060b18]/90 border-b border-white/[0.06]" style={{ backdropFilter: "blur(12px)" }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={onBack} className="flex items-center gap-1.5 text-[#6b7fa3] hover:text-white text-sm transition-colors min-h-[40px] shrink-0">
              <ArrowLeft size={15} />
              <span className="hidden sm:inline">Dashboard</span>
            </button>
            <span className="text-white/10 hidden sm:inline">·</span>
            <div className="hidden sm:flex items-center gap-2">
              <Users size={14} className="text-[#00c9a7]" />
              <span className="text-white font-medium text-sm">All Users</span>
            </div>
            <span className="sm:hidden text-white font-medium text-sm">All Users</span>
          </div>
          <span className="text-[#6b7fa3] text-xs sm:text-sm">{filtered.length} of {users.length}</span>
        </div>
      </header>

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-5 sm:py-8 space-y-4 sm:space-y-5">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#3d4f6a]" />
          <input type="text" placeholder="Search by name, email, or role…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-full bg-[#0c1428] border border-white/[0.07] text-white rounded-xl pl-9 pr-4 py-2.5 text-sm placeholder:text-[#3d4f6a] focus:outline-none focus:border-[#00c9a7]/30 transition-all" />
        </div>

        <div className="space-y-2.5 sm:space-y-3">
          {filtered.length === 0 && <div className="text-center py-14 text-[#6b7fa3] text-sm">No users match your search</div>}
          {filtered.map((user) => (
            <div key={user.id} className="bg-[#0c1428] hover:bg-[#0f1e3d] border border-white/[0.06] hover:border-[#00c9a7]/20 rounded-2xl p-4 sm:p-5 transition-all group">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ background: user.avatarColor + "15", border: `1px solid ${user.avatarColor}40`, color: user.avatarColor }}>
                  <span className="text-sm font-bold">{user.initials}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
                    <p className="text-white text-sm font-medium truncate">{user.name}</p>
                    <div className="hidden sm:flex items-center gap-2">{userKycBadge(user.kycStatus)}</div>
                  </div>
                  <p className="text-[#6b7fa3] text-xs truncate">{user.email}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className={`w-2 h-2 rounded-full ${user.sessionActive ? "bg-emerald-400" : "bg-[#2a3a52]"}`} />
                  <button onClick={() => onImpersonate(user)} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0a1020] hover:bg-[#00c9a7]/10 border border-white/[0.06] rounded-lg text-[#6b7fa3] hover:text-[#00c9a7] text-xs font-medium transition-all"><ChevronRight size={12} />Access</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Afdani Driver tab ──────────────────────────────────────────────────────
// Same list/detail/KYC-review shape as the marketplace KYC queue above, for
// the separate TransportDrivers/TransportDriverKYCRequests identity space.
function DriversListView({
  drivers,
  sosAlerts,
  onBack,
  onSelectDriver,
  onResolveSOS,
}: {
  drivers: DriverSummary[];
  sosAlerts: SosAlert[];
  onBack: () => void;
  onSelectDriver: (d: DriverSummary) => void;
  onResolveSOS: (alertId: number) => void;
}) {
  const [filter, setFilter] = useState<"all" | DriverKycStatus>("all");
  const [search, setSearch] = useState("");
  const [showMap, setShowMap] = useState(false);

  const openAlerts = sosAlerts.filter((a) => a.status === "open");
  const liveDrivers: LiveDriverPoint[] = drivers
    .filter((d) => d.isOnline && d.currentLat != null && d.currentLng != null)
    .map((d) => ({ driverId: d.driverId, name: d.name, lat: d.currentLat as number, lng: d.currentLng as number, isOnTrip: !!d.activeBookingId, vehicleType: d.vehicleType }));

  const filtered = drivers.filter((d) => {
    const matchFilter = filter === "all" || d.kycStatus === filter;
    const q = search.trim().toLowerCase();
    const matchSearch = q === "" || d.name.toLowerCase().includes(q) || d.phone.toLowerCase().includes(q) || (d.vehicleNumber || "").toLowerCase().includes(q);
    return matchFilter && matchSearch;
  });

  const online = drivers.filter((d) => d.isOnline).length;
  const pending = drivers.filter((d) => d.kycStatus === "pending").length;
  const verified = drivers.filter((d) => d.kycStatus === "verified").length;

  return (
    <div className="min-h-screen bg-[#060b18] font-['Inter',sans-serif] pb-20 sm:pb-0">
      <div className="fixed inset-0 opacity-[0.025]" style={{ backgroundImage: "linear-gradient(#00c9a7 1px, transparent 1px), linear-gradient(90deg, #00c9a7 1px, transparent 1px)", backgroundSize: "60px 60px" }} />

      <header className="sticky top-0 z-20 bg-[#060b18]/90 border-b border-white/[0.06]" style={{ backdropFilter: "blur(12px)" }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-12 sm:h-14 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={onBack} className="flex items-center gap-1.5 text-[#6b7fa3] hover:text-white text-sm transition-colors min-h-[40px] shrink-0">
              <ArrowLeft size={15} />
              <span className="hidden sm:inline">Dashboard</span>
            </button>
            <span className="text-white/10 hidden sm:inline">·</span>
            <div className="hidden sm:flex items-center gap-2">
              <Truck size={14} className="text-[#00c9a7]" />
              <span className="text-white font-medium text-sm">Afdani Driver</span>
            </div>
            <span className="sm:hidden text-white font-medium text-sm">Drivers</span>
          </div>
          {pending > 0 && <span className="bg-amber-500 text-[#060b18] text-[11px] font-bold px-2 py-0.5 rounded-full">{pending} pending</span>}
        </div>
      </header>

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-5 sm:py-8 space-y-4 sm:space-y-6">
        {openAlerts.length > 0 && (
          <div className="bg-red-500/[0.06] border border-red-500/25 rounded-2xl overflow-hidden">
            <div className="px-4 sm:px-5 py-3 border-b border-red-500/20 flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
              </span>
              <h2 className="text-red-400 text-sm font-semibold">🆘 {openAlerts.length} open SOS alert{openAlerts.length > 1 ? "s" : ""}</h2>
            </div>
            <div className="divide-y divide-red-500/10">
              {openAlerts.map((a) => (
                <div key={a.alertId} className="px-4 sm:px-5 py-3.5 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium">{a.driverName}{a.driverPhone ? ` · ${a.driverPhone}` : ""}</p>
                    <p className="text-[#a0aec0] text-xs mt-0.5">
                      Trip {a.bookingCode || `#${a.bookingId}`} · {a.pickupAddress || "Unknown pickup"}
                    </p>
                    {a.notes && <p className="text-[#6b7fa3] text-xs mt-0.5 italic">"{a.notes}"</p>}
                    <p className="text-[#3d4f6a] text-[11px] mt-1">{new Date(a.createdAt).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {a.lat != null && a.lng != null && (
                      <a
                        href={`https://www.google.com/maps?q=${a.lat},${a.lng}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#0a1020] hover:bg-white/[0.06] border border-white/[0.08] rounded-lg text-[#a0aec0] text-xs font-medium transition-all"
                      >
                        <MapPin size={12} />
                        Locate
                      </a>
                    )}
                    <button
                      onClick={() => onResolveSOS(a.alertId)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/25 rounded-lg text-red-400 text-xs font-semibold transition-all"
                    >
                      Mark Resolved
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          <span className="text-[#6b7fa3] text-xs">{liveDrivers.length} online driver{liveDrivers.length === 1 ? "" : "s"} with live location</span>
          <button
            onClick={() => setShowMap((v) => !v)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all min-h-[36px] ${
              showMap ? "bg-[#00c9a7] text-[#060b18]" : "bg-[#0c1428] border border-white/[0.07] text-[#6b7fa3] hover:text-white"
            }`}
          >
            <MapPin size={13} />
            {showMap ? "Hide live map" : "Show live map"}
          </button>
        </div>

        {showMap && (
          <div className="h-[50vh] min-h-[280px] rounded-2xl overflow-hidden border border-white/[0.06]">
            <AdminDriverMap drivers={liveDrivers} onSelectDriver={(id) => { const d = drivers.find((x) => x.driverId === id); if (d) onSelectDriver(d); }} />
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-[#0c1428] border border-white/[0.06] rounded-2xl p-3">
            <div className="flex items-center gap-1 mb-1">
              <Truck size={12} className="text-[#00c9a7]" />
              <span className="text-[#6b7fa3] text-[11px]">Total</span>
            </div>
            <p className="text-xl sm:text-2xl font-semibold text-white">{drivers.length}</p>
          </div>
          <div className="bg-[#0c1428] border border-emerald-500/10 rounded-2xl p-3">
            <div className="flex items-center gap-1 mb-1">
              <span className="h-2 w-2 rounded-full bg-emerald-400" />
              <span className="text-[#6b7fa3] text-[11px]">Online</span>
            </div>
            <p className="text-xl sm:text-2xl font-semibold text-emerald-400">{online}</p>
          </div>
          <div className="bg-[#0c1428] border border-amber-500/10 rounded-2xl p-3">
            <div className="flex items-center gap-1 mb-1">
              <Clock size={12} className="text-amber-400" />
              <span className="text-[#6b7fa3] text-[11px]">Pending KYC</span>
            </div>
            <p className="text-xl sm:text-2xl font-semibold text-amber-400">{pending}</p>
          </div>
          <div className="bg-[#0c1428] border border-white/[0.06] rounded-2xl p-3">
            <div className="flex items-center gap-1 mb-1">
              <CheckCircle2 size={12} className="text-[#00c9a7]" />
              <span className="text-[#6b7fa3] text-[11px]">Verified</span>
            </div>
            <p className="text-xl sm:text-2xl font-semibold text-white">{verified}</p>
          </div>
        </div>

        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#3d4f6a]" />
          <input
            type="text"
            placeholder="Search by name, phone or vehicle number…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-[#0c1428] border border-white/[0.07] text-white rounded-xl pl-9 pr-4 py-2.5 text-sm placeholder:text-[#3d4f6a] focus:outline-none focus:border-[#00c9a7]/30 transition-all"
          />
        </div>

        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none -mx-4 px-4 sm:mx-0 sm:px-0">
          {(["all", "pending", "verified", "rejected", "not_submitted"] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3.5 py-2 rounded-xl text-xs font-medium transition-all whitespace-nowrap min-h-[36px] ${
                filter === f ? "bg-[#00c9a7] text-[#060b18]" : "bg-[#0c1428] border border-white/[0.07] text-[#6b7fa3] hover:text-white"
              }`}
            >
              {f === "not_submitted" ? "Not submitted" : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        <div className="space-y-2.5 sm:space-y-3">
          {filtered.length === 0 && <div className="text-center py-14 text-[#6b7fa3] text-sm">No drivers match your filter</div>}
          {filtered.map((d) => (
            // A <button> can't legally contain the nested Call <button> below
            // (interactive content can't nest) — role="button" on a div gets
            // the same click-to-open-detail behavior with valid markup.
            <div
              key={d.driverId}
              role="button"
              tabIndex={0}
              onClick={() => onSelectDriver(d)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelectDriver(d); } }}
              className="w-full bg-[#0c1428] active:bg-[#0f1e3d] hover:bg-[#0f1e3d] border border-white/[0.06] hover:border-[#00c9a7]/20 rounded-2xl p-4 sm:p-5 text-left transition-all group cursor-pointer"
            >
              <div className="flex items-center gap-3 sm:gap-4">
                <div className={`relative w-10 h-10 sm:w-11 sm:h-11 rounded-2xl flex items-center justify-center shrink-0 ${d.kycStatus === "pending" ? "bg-amber-500/15 border border-amber-500/25" : "bg-white/5 border border-white/[0.07]"}`}>
                  <span className={`text-sm font-bold ${d.kycStatus === "pending" ? "text-amber-400" : "text-[#6b7fa3]"}`}>
                    {d.name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
                  </span>
                  {!d.isActive && (
                    <span className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 border border-[#0c1428]">
                      <Ban size={9} className="text-white" />
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
                    <p className="text-white text-sm font-medium truncate">{d.name}</p>
                    <div className="hidden sm:flex items-center gap-2">{userKycBadge(d.kycStatus)}</div>
                  </div>
                  <p className="text-[#6b7fa3] text-xs truncate">
                    {d.phone} · {d.vehicleType || "No vehicle"}{d.vehicleNumber ? ` · ${d.vehicleNumber}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="hidden sm:flex items-center gap-1 text-[#a0aec0] text-xs">
                    <Star size={11} className="fill-amber-400 text-amber-400" />
                    {d.rating != null ? d.rating.toFixed(1) : "—"}
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); window.location.href = `tel:${d.phone.replace(/[^\d+]/g, "")}`; }}
                    aria-label={`Call ${d.name}`}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#00c9a7]/10 hover:bg-[#00c9a7]/20 text-[#00c9a7] transition-all"
                  >
                    <Phone size={12} />
                  </button>
                  <div className={`w-2 h-2 rounded-full ${d.isOnline ? "bg-emerald-400" : "bg-[#2a3a52]"}`} />
                  <ChevronRight size={14} className="text-[#3d4f6a]" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DriverDetailView({
  driver,
  kyc,
  onBack,
  onReviewKyc,
  onToggleActive,
  onAccessDriver,
  accessingDriver,
}: {
  driver: DriverDetail;
  kyc: DriverKycDocs | null;
  onBack: () => void;
  onReviewKyc: (action: "verify" | "reject" | "pending", notes?: string) => void;
  onToggleActive: (isActive: boolean) => void;
  onAccessDriver: () => void;
  accessingDriver: boolean;
}) {
  const [rejectNotes, setRejectNotes] = useState("");
  const [showRejectInput, setShowRejectInput] = useState(false);

  const documentImages = kyc
    ? [
        { label: "Driving License", src: normalizeDocumentImageUrl(kyc.drivingLicenseImage) },
        { label: "Aadhaar Card", src: normalizeDocumentImageUrl(kyc.aadhaarImage) },
        { label: "PAN Card", src: normalizeDocumentImageUrl(kyc.panImage) },
        { label: "RC Book", src: normalizeDocumentImageUrl(kyc.rcImage) },
        { label: "Selfie", src: normalizeDocumentImageUrl(kyc.selfieImage) },
        { label: "Insurance", src: normalizeDocumentImageUrl(kyc.insuranceImage) },
        { label: "Permit", src: normalizeDocumentImageUrl(kyc.permitImage) },
        { label: "Profile Photo", src: normalizeDocumentImageUrl(kyc.profilePhotoImage) },
      ].filter((i) => Boolean(i.src))
    : [];

  const status = kyc?.status ?? driver.kycStatus;

  const statCards: { label: string; value: string | number; icon: React.ElementType; color: string }[] = [
    { label: "Rating", value: driver.rating != null ? driver.rating.toFixed(1) : "—", icon: Star, color: "#f59e0b" },
    { label: "Trips", value: driver.totalTrips, icon: TrendingUp, color: "#00c9a7" },
    { label: "Accepted", value: driver.acceptedTrips, icon: CheckCircle2, color: "#10b981" },
    { label: "Declined", value: driver.declinedTrips, icon: XCircle, color: "#f59e0b" },
    { label: "Cancelled", value: driver.cancelledTrips, icon: Ban, color: "#ef4444" },
    { label: "Wallet", value: `₹${driver.walletBalance.toFixed(0)}`, icon: Wallet, color: "#4f46e5" },
  ];

  return (
    <div className="min-h-screen bg-[#060b18] font-['Inter',sans-serif] pb-20 sm:pb-0">
      <div className="fixed inset-0 opacity-[0.025]" style={{ backgroundImage: "linear-gradient(#00c9a7 1px, transparent 1px), linear-gradient(90deg, #00c9a7 1px, transparent 1px)", backgroundSize: "60px 60px" }} />

      <header className="sticky top-0 z-20 bg-[#060b18]/90 border-b border-white/[0.06]" style={{ backdropFilter: "blur(12px)" }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
          <button onClick={onBack} className="flex items-center gap-1.5 text-[#6b7fa3] hover:text-white text-sm transition-colors min-h-[40px]">
            <ArrowLeft size={15} />
            <span className="hidden sm:inline">Back to Drivers</span>
            <span className="sm:hidden">Drivers</span>
          </button>
          <span className="text-white/10">·</span>
          <p className="text-white text-sm font-medium truncate">{driver.name}</p>
        </div>
      </header>

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-5 sm:py-8 space-y-4 sm:space-y-5">
        <div className="bg-[#0c1428] border border-white/[0.07] rounded-2xl p-4 sm:p-6">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center shrink-0 ${status === "pending" ? "bg-amber-500/15 border border-amber-500/25" : "bg-white/5 border border-white/[0.07]"}`}>
              <span className={`text-base sm:text-xl font-bold ${status === "pending" ? "text-amber-400" : "text-[#6b7fa3]"}`}>
                {driver.name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-white text-base sm:text-xl font-semibold truncate">{driver.name}</h1>
                <div className={`w-2 h-2 rounded-full ${driver.isOnline ? "bg-emerald-400" : "bg-[#2a3a52]"}`} title={driver.isOnline ? "Online" : "Offline"} />
              </div>
              <p className="text-[#6b7fa3] text-xs sm:text-sm truncate">
                {driver.phone}{driver.email ? ` · ${driver.email}` : ""}
              </p>
              {(driver.address || driver.city) && (
                <p className="text-[#3d4f6a] text-xs truncate mt-0.5">
                  {[driver.address, driver.city, driver.pincode].filter(Boolean).join(", ")}
                </p>
              )}
              {/* Nothing routes admin<->driver in-app (that's customer<->driver
                  only) — these just hand off to whatever's actually installed
                  on the admin's own device: the OS dialer, WhatsApp, or email. */}
              <div className="flex flex-wrap gap-2 mt-3">
                {driver.phone && (
                  <a
                    href={`tel:${driver.phone.replace(/[^\d+]/g, "")}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#00c9a7]/10 hover:bg-[#00c9a7]/20 border border-[#00c9a7]/25 rounded-lg text-[#00c9a7] text-xs font-medium transition-all"
                  >
                    <Phone size={12} />
                    Call
                  </a>
                )}
                {driver.phone && (
                  <a
                    href={`https://wa.me/${driver.phone.replace(/[^\d]/g, "")}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0a1020] hover:bg-white/[0.06] border border-white/[0.08] rounded-lg text-[#a0aec0] text-xs font-medium transition-all"
                  >
                    <MessageCircle size={12} />
                    WhatsApp
                  </a>
                )}
                {driver.email && (
                  <a
                    href={`mailto:${driver.email}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0a1020] hover:bg-white/[0.06] border border-white/[0.08] rounded-lg text-[#a0aec0] text-xs font-medium transition-all"
                  >
                    <Mail size={12} />
                    Email
                  </a>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {userKycBadge(status)}
                {!driver.isActive && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border bg-red-500/10 text-red-400 border-red-500/20">
                    <Ban size={9} />
                    Suspended
                  </span>
                )}
              </div>
              <p className="text-[#3d4f6a] text-[11px] mt-1.5 font-mono">
                Driver #{driver.driverId} · Joined {new Date(driver.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
              </p>
              <button
                type="button"
                onClick={onAccessDriver}
                disabled={accessingDriver || !driver.isActive}
                className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-lg bg-[#00c9a7] hover:bg-[#00b396] disabled:opacity-50 disabled:cursor-not-allowed text-[#060b18] text-xs font-semibold transition-all"
              >
                <ExternalLink size={12} />
                {accessingDriver ? "Opening driver account…" : "Access driver account"}
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          {statCards.map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="bg-[#0c1428] border border-white/[0.06] rounded-2xl p-3">
              <div className="flex items-center gap-1 mb-1">
                <Icon size={11} style={{ color }} />
                <span className="text-[#6b7fa3] text-[10px]">{label}</span>
              </div>
              <p className="text-base sm:text-lg font-semibold text-white">{value}</p>
            </div>
          ))}
        </div>

        <div className="bg-[#0c1428] border border-white/[0.06] rounded-2xl overflow-hidden">
          <div className="px-4 sm:px-5 py-3 border-b border-white/[0.05]">
            <h2 className="text-[#a0aec0] text-[10px] font-medium tracking-widest uppercase">Vehicle</h2>
          </div>
          <div className="p-3 sm:p-4 grid grid-cols-2 gap-2">
            {[
              { label: "Type", value: driver.vehicleType },
              { label: "Number", value: driver.vehicleNumber },
              { label: "Model", value: driver.vehicleModel },
              { label: "Color", value: driver.vehicleColor },
              { label: "Capacity", value: driver.vehicleCapacityKg != null ? `${driver.vehicleCapacityKg} kg` : null },
            ].map(({ label, value }) => (
              <div key={label} className="bg-[#0a1020] rounded-xl p-2.5">
                <p className="text-[#6b7fa3] text-[11px]">{label}</p>
                <p className="text-white text-sm font-medium mt-0.5 break-words">{value || "—"}</p>
              </div>
            ))}
          </div>
        </div>

        {documentImages.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {documentImages.map((doc) => (
              <div key={doc.label} className="bg-[#0c1428] border border-white/[0.06] rounded-2xl overflow-hidden">
                <div className="px-4 py-3 border-b border-white/[0.05] flex items-center justify-between gap-2">
                  <p className="text-[#a0aec0] text-[10px] font-medium tracking-widest uppercase truncate">{doc.label}</p>
                  <a
                    href={doc.src}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/[0.08] text-[#6b7fa3] hover:border-[#00c9a7] hover:text-[#00c9a7] transition-colors shrink-0"
                    aria-label={`Open ${doc.label} full image`}
                  >
                    <ExternalLink size={12} />
                  </a>
                </div>
                <div className="p-3">
                  <a href={doc.src} target="_blank" rel="noreferrer" className="block overflow-hidden rounded-xl">
                    <img
                      src={doc.src}
                      alt={doc.label}
                      loading="lazy"
                      className="w-full h-28 sm:h-36 object-cover rounded-xl border border-white/[0.05]"
                      onError={(event) => { event.currentTarget.style.display = "none"; }}
                    />
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}

        {!kyc && (
          <div className="bg-[#0c1428] border border-white/[0.06] rounded-2xl p-5 text-center text-[#6b7fa3] text-sm">
            This driver hasn't submitted any KYC documents yet.
          </div>
        )}

        {kyc?.notes && (
          <div className="bg-[#0c1428] border border-white/[0.06] rounded-2xl p-4">
            <p className="text-[#a0aec0] text-[10px] font-medium tracking-widest uppercase mb-1">Reviewer notes</p>
            <p className="text-white text-sm">{kyc.notes}</p>
          </div>
        )}

        {kyc && status === "pending" && (
          <div className="bg-[#0c1428] border border-white/[0.06] rounded-2xl p-4 sm:p-5">
            {showRejectInput ? (
              <div className="space-y-3">
                <textarea
                  value={rejectNotes}
                  onChange={(e) => setRejectNotes(e.target.value)}
                  placeholder="Reason for rejection (shown to the driver)…"
                  className="w-full bg-[#0a1020] border border-white/[0.08] text-white rounded-xl px-3 py-2.5 text-sm placeholder:text-[#3d4f6a] focus:outline-none focus:border-[#00c9a7]/30 transition-all min-h-[80px]"
                />
                <div className="flex gap-3">
                  <button onClick={() => { setShowRejectInput(false); setRejectNotes(""); }} className="flex-1 min-h-[44px] bg-[#1f2937] border border-white/[0.08] text-[#9ca3af] rounded-xl text-sm font-medium transition-all">Cancel</button>
                  <button
                    onClick={() => { onReviewKyc("reject", rejectNotes); setShowRejectInput(false); setRejectNotes(""); }}
                    className="flex-1 min-h-[44px] bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl text-sm font-medium transition-all"
                  >
                    Confirm Reject
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
                <div>
                  <p className="text-white font-medium text-sm">Ready to make a decision?</p>
                  <p className="text-[#6b7fa3] text-xs mt-0.5">This updates the driver's ability to go online and accept trips.</p>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setShowRejectInput(true)} className="flex-1 sm:flex-initial px-6 py-2.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400 rounded-xl text-sm font-medium transition-all">Reject</button>
                  <button onClick={() => onReviewKyc("verify")} className="flex-1 sm:flex-initial px-6 py-2.5 bg-[#00c9a7] hover:bg-[#00b396] text-[#060b18] rounded-xl text-sm font-semibold transition-all">Verify &amp; Approve</button>
                </div>
              </div>
            )}
          </div>
        )}

        {kyc && status !== "pending" && (
          <div className="flex flex-col sm:flex-row bg-[#0c1428] border border-white/[0.06] rounded-2xl p-5 items-start sm:items-center justify-between gap-4">
            <div>
              <p className="text-white font-medium text-sm">{status === "verified" ? "Reset verification status" : "Reopen rejected request"}</p>
              <p className="text-[#6b7fa3] text-xs mt-0.5">
                {status === "verified" ? "Verified drivers can be reopened for review or rejected again." : "Rejected requests can also be reopened for review or approved directly."}
              </p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => onReviewKyc("pending")} className="px-6 py-2.5 bg-[#1f2937] hover:bg-[#111827] border border-white/[0.08] text-[#9ca3af] rounded-xl text-sm font-medium transition-all">Reset to Pending</button>
              {status === "verified" ? (
                <button onClick={() => setShowRejectInput(true)} className="px-6 py-2.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400 rounded-xl text-sm font-medium transition-all">Reject</button>
              ) : (
                <button onClick={() => onReviewKyc("verify")} className="px-6 py-2.5 bg-[#00c9a7] hover:bg-[#00b396] text-[#060b18] rounded-xl text-sm font-semibold transition-all">Verify &amp; Approve</button>
              )}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between gap-4 bg-[#0c1428] border border-white/[0.06] rounded-2xl p-4 sm:p-5">
          <div>
            <p className="text-white font-medium text-sm">{driver.isActive ? "Suspend this driver" : "Reactivate this driver"}</p>
            <p className="text-[#6b7fa3] text-xs mt-0.5">{driver.isActive ? "Suspended drivers can't log in or accept trips." : "Reactivating restores login and trip access."}</p>
          </div>
          <button
            onClick={() => onToggleActive(!driver.isActive)}
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium transition-all shrink-0 ${
              driver.isActive ? "bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400" : "bg-[#00c9a7] hover:bg-[#00b396] text-[#060b18]"
            }`}
          >
            {driver.isActive ? (<><Ban size={14} className="inline mr-1.5 -mt-0.5" />Suspend</>) : (<><RotateCcw size={14} className="inline mr-1.5 -mt-0.5" />Reactivate</>)}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboardLocal({ onLogout, initialUsers, initialKycRequests }: Props) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>(initialUsers ?? []);
  const [kycRequests, setKycRequests] = useState<KYCRequest[]>(initialKycRequests ?? []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [mainView, setMainView] = useState<MainView>("dashboard");
  const [selectedRequest, setSelectedRequest] = useState<KYCRequest | null>(null);
  const [problemReports, setProblemReports] = useState<ProblemReport[]>([]);
  const [loadingProblemReports, setLoadingProblemReports] = useState(false);
  const [problemReportsError, setProblemReportsError] = useState<string | null>(null);
  const [drivers, setDrivers] = useState<DriverSummary[]>([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [driversError, setDriversError] = useState<string | null>(null);
  const [selectedDriver, setSelectedDriver] = useState<DriverDetail | null>(null);
  const [selectedDriverKyc, setSelectedDriverKyc] = useState<DriverKycDocs | null>(null);
  const [accessingDriver, setAccessingDriver] = useState(false);
  const [sosAlerts, setSosAlerts] = useState<SosAlert[]>([]);

  const pendingKYC = kycRequests.filter((r) => r.status === "pending").length;
  const pendingDriverKYC = drivers.filter((d) => d.kycStatus === "pending").length;
  const openSOSCount = sosAlerts.filter((a) => a.status === "open").length;

  const filteredUsers = users.filter((u) => u.name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase()));

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    // users
    const userEndpoints = [`${API_BASE_URL}/api/admin/all-users`, `${API_BASE_URL}/api/admin/users`, `${API_BASE_URL}/api/users`];
    for (const url of userEndpoints) {
      try {
        const res = await adminFetch(url);
        if (!res.ok) continue;
        const payload = await res.json();
        console.debug("AdminDashboardLocal: fetched users from", url, payload?.length ?? (payload?.users?.length));
        const list = Array.isArray(payload?.users) ? payload.users : Array.isArray(payload) ? payload : (payload?.data && Array.isArray(payload.data) ? payload.data : null);
        if (!list) continue;
        setUsers(list.map((item: any) => ({
          id: String(item.opID ?? item.id ?? item.userId ?? item.user_id ?? item._id ?? item.email ?? item.name ?? Math.random()),
          name: item.name ?? item.fullName ?? ((`${item.firstName || ""} ${item.lastName || ""}`).trim() || item.username || "Unknown"),
          email: item.email ?? item.emailAddress ?? "",
          role: item.role ?? "Trader",
          joinedAt: item.joinedAt ?? item.createdAt ?? new Date().toISOString(),
          lastActive: item.lastActive ?? item.lastSeen ?? new Date().toISOString(),
          kycStatus: item.kycStatus ?? item.kyc_status ?? "not_submitted",
          sessionActive: item.activeSession ?? item.sessionActive ?? false,
          initials: (item.name || item.fullName || item.username || "").split(" ").map((p: string) => p[0]).slice(0, 2).join("").toUpperCase(),
          avatarColor: item.avatarColor ?? "#3b82f6",
          tradeVolume: item.tradeVolume ?? item.volume ?? "$0",
          country: item.country ?? item.nationality ?? "",
        })));
        break;
      } catch (e) {
        // try next
      }
    }

    const kycEndpoints = [KYC_API_PATH];
    for (const url of kycEndpoints) {
      try {
        const res = await adminFetch(url);
        if (!res.ok) continue;
        const payload = await res.json();
        console.debug("AdminDashboardLocal: fetched kyc candidates from", url, payload?.requests?.length ?? payload?.length);
        const list = Array.isArray(payload?.requests) ? payload.requests : Array.isArray(payload) ? payload : (payload?.data && Array.isArray(payload.data) ? payload.data : null);
        if (!list) continue;
        setKycRequests(list.map((r: any) => ({
          id: String(r.id ?? r.VerificationID ?? r.requestId ?? r._id ?? Math.random()),
          userId: String(r.opID ?? r.userId ?? r.user_id ?? r.user?.id ?? ""),
          userName: r.fullName || (`${r.firstName || ""} ${r.lastName || ""}`.trim() || r.username || r.user?.name || ""),
          email: r.email ?? r.user?.email ?? "",
          status: (r.status ?? r.Status ?? "pending").toLowerCase(),
          submittedAt: r.submittedAt ?? r.SubmittedAt ?? r.createdAt ?? new Date().toISOString(),
          fullLegalName: r.fullName ?? r.FullName ?? "",
          dateOfBirth: r.dateOfBirth ?? r.DOB ?? "",
          nationality: r.nationality ?? r.country ?? "",
          idType: r.idType ?? r.documentType ?? "PAN",
          idNumber: r.idNumber ?? r.panNumber ?? r.PANNumber ?? "",
          idExpiry: r.idExpiry ?? r.documentExpiry ?? "",
          address: r.address ?? r.currentAddress ?? r.CurrentAddress ?? "",
          city: r.city ?? "",
          country: r.country ?? "",
          postalCode: r.postalCode ?? r.zip ?? "",
          occupation: r.occupation ?? r.job ?? "",
          sourceOfFunds: r.sourceOfFunds ?? r.funds ?? "",
          phone: r.phone ?? r.phoneNumber ?? r.PhoneNumber ?? "",
          riskLevel: r.riskLevel ?? r.risk ?? "Low",
          annualIncome: r.annualIncome ?? r.income ?? "",
          panFrontImage: r.panFrontImage ?? r.PanFrontImage ?? "",
          panBackImage: r.panBackImage ?? r.PanBackImage ?? "",
          aadhaarFrontImage: r.aadhaarFrontImage ?? r.AadhaarFrontImage ?? "",
          selfieImage: r.selfieImage ?? r.SelfieImage ?? "",
          notes: r.notes ?? r.Notes ?? "",
        })));
        break;
      } catch (e) {
        // try next
      }
    }

    setLoading(false);
  };

  useEffect(() => {
    void fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch KYC requests when user opens the KYC queue to ensure freshness
  useEffect(() => {
    if (mainView !== "kyc-queue") return;
    const fetchKycRequests = async () => {
      console.debug("AdminDashboardLocal: fetching KYC requests for queue view");
      const kycEndpoints = [KYC_API_PATH];
      for (const url of kycEndpoints) {
        try {
          const res = await adminFetch(url);
          if (!res.ok) continue;
          const payload = await res.json();
          console.debug("AdminDashboardLocal: fetched kyc from", url, payload?.requests?.length ?? payload?.length ?? 0);
          const list = Array.isArray(payload?.requests) ? payload.requests : Array.isArray(payload) ? payload : (payload?.data && Array.isArray(payload.data) ? payload.data : null);
          if (!list) continue;
          setKycRequests(list.map((r: any) => ({
            id: String(r.id ?? r.VerificationID ?? r.requestId ?? Math.random()),
            userId: String(r.opID ?? r.userId ?? r.user_id ?? r.user?.id ?? ""),
            userName: r.fullName || (`${r.firstName || ""} ${r.lastName || ""}`.trim() || r.username || r.user?.name || ""),
            email: r.email ?? r.user?.email ?? "",
            status: String(r.status ?? r.Status ?? "pending").trim().toLowerCase(),
            submittedAt: r.submittedAt ?? r.SubmittedAt ?? r.createdAt ?? new Date().toISOString(),
            fullLegalName: r.fullName ?? r.FullName ?? "",
            dateOfBirth: r.dateOfBirth ?? r.DOB ?? "",
            nationality: r.nationality ?? r.country ?? "",
            idType: r.idType ?? r.documentType ?? "PAN",
            idNumber: r.idNumber ?? r.panNumber ?? r.PANNumber ?? "",
            idExpiry: r.idExpiry ?? r.documentExpiry ?? "",
            address: r.address ?? r.currentAddress ?? r.CurrentAddress ?? "",
            city: r.city ?? "",
            country: r.country ?? "",
            postalCode: r.postalCode ?? r.zip ?? "",
            occupation: r.occupation ?? r.job ?? "",
            sourceOfFunds: r.sourceOfFunds ?? r.funds ?? "",
            phone: r.phone ?? r.phoneNumber ?? r.PhoneNumber ?? "",
            riskLevel: r.riskLevel ?? r.risk ?? "Low",
            annualIncome: r.annualIncome ?? r.income ?? "",
            panFrontImage: r.panFrontImage ?? r.PanFrontImage ?? "",
            panBackImage: r.panBackImage ?? r.PanBackImage ?? "",
            aadhaarFrontImage: r.aadhaarFrontImage ?? r.AadhaarFrontImage ?? "",
            selfieImage: r.selfieImage ?? r.SelfieImage ?? "",
            notes: r.notes ?? r.Notes ?? "",
          })));
          break;
        } catch (e) {
          // try next
        }
      }
    };

    void fetchKycRequests();
  }, [mainView]);

  const loadProblemReports = async () => {
    setLoadingProblemReports(true);
    setProblemReportsError(null);

    try {
      const response = await supportService.getProblemReportsAdmin();
      setProblemReports(response.data || []);
    } catch (e) {
      console.warn("AdminDashboardLocal: failed to load problem reports", e);
      setProblemReportsError("Unable to fetch problem reports at this time.");
      setProblemReports([]);
    } finally {
      setLoadingProblemReports(false);
    }
  };

  useEffect(() => {
    if (mainView !== "problem-reports") return;
    void loadProblemReports();
  }, [mainView]);

  const loadDrivers = async () => {
    setLoadingDrivers(true);
    setDriversError(null);
    try {
      const res = await adminFetch(DRIVERS_API_PATH);
      const payload = await res.json();
      if (!res.ok || !payload?.success) throw new Error(payload?.message || "Failed to load drivers.");
      setDrivers(payload.drivers || []);
    } catch (e) {
      console.warn("AdminDashboardLocal: failed to load drivers", e);
      setDriversError("Unable to fetch drivers at this time.");
      setDrivers([]);
    } finally {
      setLoadingDrivers(false);
    }
  };

  useEffect(() => {
    if (mainView !== "drivers-list") return;
    void loadDrivers();
  }, [mainView]);

  // Eager initial load (not just on tab-open) so the nav's pending-KYC badge
  // is correct the moment the dashboard mounts, same as the marketplace KYC
  // badge already gets via fetchData() above.
  useEffect(() => {
    void loadDrivers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSOSAlerts = async () => {
    try {
      const res = await adminFetch(SOS_API_PATH);
      const payload = await res.json();
      if (!res.ok || !payload?.success) throw new Error(payload?.message || "Failed to load SOS alerts.");
      setSosAlerts(payload.alerts || []);
    } catch (e) {
      console.warn("AdminDashboardLocal: failed to load SOS alerts", e);
    }
  };

  // Safety alerts are urgent enough to poll continuously in the background,
  // not just while the Drivers tab happens to be open — this is what keeps
  // the red nav badge honest from anywhere in the panel. This admin panel
  // has no socket client at all today, so a tight poll (matches the driver
  // app's own ~8s location-ping cadence) is the realistic "live" here rather
  // than a genuine push, without taking on a whole new socket subsystem for it.
  useEffect(() => {
    void loadSOSAlerts();
    const iv = setInterval(loadSOSAlerts, LIVE_POLL_MS);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Drivers' live locations only need to refresh while the map is actually
  // being looked at — the drivers-list view already reloads on tab-open, this
  // just keeps it moving smoothly while that tab stays active.
  useEffect(() => {
    if (mainView !== "drivers-list") return;
    const iv = setInterval(loadDrivers, LIVE_POLL_MS);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainView]);

  const handleResolveSOS = async (alertId: number) => {
    try {
      const res = await adminFetch(`${SOS_API_PATH}/${alertId}/resolve`, { method: "POST" });
      const payload = await res.json();
      if (!res.ok || !payload?.success) throw new Error(payload?.message || "Failed to resolve alert.");
      setSosAlerts((prev) => prev.map((a) => (a.alertId === alertId ? { ...a, status: "resolved", resolvedAt: new Date().toISOString() } : a)));
      toast.success("SOS alert marked resolved.");
    } catch (e) {
      console.warn("AdminDashboardLocal: failed to resolve SOS alert", e);
      toast.error("Unable to resolve this alert.");
    }
  };

  const loadDriverDetail = async (driverId: number) => {
    try {
      const res = await adminFetch(`${DRIVERS_API_PATH}/${driverId}`);
      const payload = await res.json();
      if (!res.ok || !payload?.success) throw new Error(payload?.message || "Failed to load driver.");
      setSelectedDriver(payload.driver);
      setSelectedDriverKyc(payload.kyc || null);
    } catch (e) {
      console.warn("AdminDashboardLocal: failed to load driver detail", e);
      toast.error("Unable to load this driver's details.");
    }
  };

  const handleSelectDriver = (d: DriverSummary) => {
    setSelectedDriver(d as DriverDetail);
    setSelectedDriverKyc(null);
    setMainView("driver-detail");
    void loadDriverDetail(d.driverId);
  };

  const handleDriverKycReview = async (action: "verify" | "reject" | "pending", notes?: string) => {
    if (!selectedDriver) return;
    try {
      const res = await adminFetch(`${DRIVERS_API_PATH}/${selectedDriver.driverId}/kyc/review`, {
        method: "POST",
        body: JSON.stringify({ action, notes }),
      });
      const payload = await res.json();
      if (!res.ok || !payload?.success) throw new Error(payload?.message || "Failed to update KYC status.");
      const nextStatus = payload.status as DriverKycStatus;
      setSelectedDriver((prev) => (prev ? { ...prev, kycStatus: nextStatus } : prev));
      setSelectedDriverKyc((prev) => (prev ? { ...prev, status: nextStatus, notes: notes ?? prev.notes } : prev));
      setDrivers((prev) => prev.map((d) => (d.driverId === selectedDriver.driverId ? { ...d, kycStatus: nextStatus } : d)));
      toast.success(action === "verify" ? "Driver verified." : action === "reject" ? "Driver rejected." : "Verification reset to pending.");
    } catch (e) {
      console.warn("AdminDashboardLocal: driver KYC review failed", e);
      toast.error("Unable to update KYC status.");
    }
  };

  const handleToggleDriverActive = async (isActive: boolean, force = false) => {
    if (!selectedDriver) return;
    const reason = window.prompt(isActive ? "Reason for reactivating this driver (required):" : "Reason for suspending this driver (required):");
    if (!reason || !reason.trim()) return;
    try {
      const res = await adminFetch(`${DRIVERS_API_PATH}/${selectedDriver.driverId}/status`, {
        method: "POST",
        body: JSON.stringify({ isActive, reason, force }),
      });
      const payload = await res.json();
      if (res.status === 409 && payload?.code === "driver_has_active_trip") {
        const confirmForce = window.confirm(
          `${payload.message}\n\nActive booking #${payload.activeBookingId} (${payload.activeBookingStatus}). Force-suspend anyway? (superadmin only)`
        );
        if (confirmForce) await handleToggleDriverActive(isActive, true);
        return;
      }
      if (!res.ok || !payload?.success) throw new Error(payload?.message || "Failed to update driver status.");
      setSelectedDriver((prev) => (prev ? { ...prev, isActive } : prev));
      setDrivers((prev) => prev.map((d) => (d.driverId === selectedDriver.driverId ? { ...d, isActive } : d)));
      toast.success(isActive ? "Driver reactivated." : "Driver suspended.");
    } catch (e) {
      console.warn("AdminDashboardLocal: driver status update failed", e);
      toast.error("Unable to update driver status.");
    }
  };

  const handleAccessDriver = async () => {
    if (!selectedDriver || accessingDriver) return;
    const reason = window.prompt("Reason for accessing this driver's account (required):");
    if (!reason || !reason.trim()) return;
    setAccessingDriver(true);
    try {
      const res = await adminFetch(`${DRIVERS_API_PATH}/${selectedDriver.driverId}/impersonate`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      });
      const payload = await res.json();
      if (!res.ok || !payload?.success || !payload.sessionToken) throw new Error(payload?.message || "Unable to access driver account.");
      const handoffUrl = `${DRIVER_APP_URL.replace(/\/$/, "")}/?adminSessionToken=${encodeURIComponent(payload.sessionToken)}`;
      window.open(handoffUrl, "_blank", "noopener,noreferrer");
      toast.success("Driver account opened in a new tab.");
    } catch (e) {
      console.warn("AdminDashboardLocal: driver impersonation failed", e);
      toast.error(e instanceof Error ? e.message : "Unable to access driver account.");
    } finally {
      setAccessingDriver(false);
    }
  };

  const loadRequestDetail = async (requestId: string) => {
    try {
      const res = await adminFetch(`${KYC_API_PATH}/${encodeURIComponent(requestId)}`);
      if (!res.ok) return;
      const payload = await res.json();
      const r = payload?.request ?? payload;
      if (!r) return;
      setSelectedRequest({
        id: String(r.id ?? r.VerificationID ?? r.requestId ?? r._id ?? requestId),
        userId: String(r.opID ?? r.userId ?? r.user_id ?? r.user?.id ?? ""),
        userName: r.fullName || (`${r.firstName || ""} ${r.lastName || ""}`.trim() || r.username || r.user?.name || ""),
        email: r.email ?? r.user?.email ?? "",
        status: (r.status ?? r.Status ?? "pending").toLowerCase(),
        submittedAt: r.submittedAt ?? r.SubmittedAt ?? r.createdAt ?? new Date().toISOString(),
        fullLegalName: r.fullName ?? r.FullName ?? "",
        dateOfBirth: r.dateOfBirth ?? r.DOB ?? "",
        nationality: r.nationality ?? r.country ?? "",
        idType: r.idType ?? r.documentType ?? "PAN",
        idNumber: r.idNumber ?? r.panNumber ?? r.PANNumber ?? "",
        idExpiry: r.idExpiry ?? r.documentExpiry ?? "",
        address: r.address ?? r.currentAddress ?? r.CurrentAddress ?? "",
        city: r.city ?? "",
        country: r.country ?? "",
        postalCode: r.postalCode ?? r.zip ?? "",
        occupation: r.occupation ?? r.job ?? "",
        sourceOfFunds: r.sourceOfFunds ?? r.funds ?? "",
        phone: r.phone ?? r.phoneNumber ?? r.PhoneNumber ?? "",
        riskLevel: r.riskLevel ?? r.risk ?? "Low",
        annualIncome: r.annualIncome ?? r.income ?? "",
        panFrontImage: r.panFrontImage ?? r.PanFrontImage ?? "",
        panBackImage: r.panBackImage ?? r.PanBackImage ?? "",
        aadhaarFrontImage: r.aadhaarFrontImage ?? r.AadhaarFrontImage ?? "",
        selfieImage: r.selfieImage ?? r.SelfieImage ?? "",
        notes: r.notes ?? r.Notes ?? "",
      });
    } catch (e) {
      console.warn("AdminDashboardLocal: failed to load request detail", e);
    }
  };

  const handleReviewRequest = (id: string, action: "verify" | "reject" | "pending") => {
    void (async () => {
      // ReviewedBy is now always the authenticated admin (server.js sets it
      // from the bearer token) — no client-supplied reviewer field needed.
      const payload = { action };
      try {
        const res = await adminFetch(`${KYC_API_PATH}/${encodeURIComponent(id)}/review`, {
          method: "POST",
          body: JSON.stringify(payload),
        });
        if (res.ok) {
          const nextStatus = action === "verify" ? "verified" : action === "reject" ? "rejected" : "pending";
          setKycRequests((prev) => prev.map((p) => (p.id === id ? { ...p, status: nextStatus } : p)));
          setSelectedRequest((prev) => prev ? { ...prev, status: nextStatus } : prev);
        }
      } catch (e) {
        console.warn("AdminDashboardLocal: review request failed", e);
      }
    })();
  };

  const handleResolveProblemReport = async (reportId: number | string) => {
    try {
      const result = await supportService.updateProblemReportStatus(reportId, "closed");
      const updated = result.data;
      setProblemReports((prev) =>
        prev.map((report) =>
          report.ReportID === Number(reportId)
            ? { ...report, Status: updated?.Status ?? "closed", UpdatedAt: updated?.UpdatedAt ?? new Date().toISOString() }
            : report,
        ),
      );
      toast.success("Report marked resolved.");
    } catch (e) {
      console.warn("AdminDashboardLocal: failed to resolve problem report", e);
      toast.error("Unable to update report status.");
    }
  };

  const handleImpersonate = async (user: User) => {
    const reason = window.prompt(`Reason for accessing ${user.name}'s account (required):`);
    if (!reason || !reason.trim()) return;
    try {
      const res = await adminFetch(`/api/admin/impersonate`, {
        method: "POST",
        body: JSON.stringify({ targetOpID: user.id || user.email || user.name, reason }),
      });
      const data = await res.json();
      if (!res.ok || !data?.success || !(data.sessionToken || data.session_token)) {
        toast.error(data?.message || "Unable to access this user's account.");
        return;
      }
      const opID = data.opID || user.id;
      login(opID, data.sessionToken || data.session_token, data.expiresAt || data.expires_at);
      navigate("/");
    } catch (e) {
      console.warn("AdminDashboardLocal: impersonation failed", e);
      toast.error("Unable to access this user's account.");
    }
  };

  const totalUsers = users.length;
  const activeSessions = users.filter((u) => u.sessionActive).length;
  const kycVerified = kycRequests.filter((r) => r.status === "verified").length;

  return (
    <div className="min-h-screen bg-[#060b18] font-['Inter',sans-serif] pb-20 sm:pb-0">
      <div className="fixed inset-0 opacity-[0.025]" style={{ backgroundImage: "linear-gradient(#00c9a7 1px, transparent 1px), linear-gradient(90deg, #00c9a7 1px, transparent 1px)", backgroundSize: "60px 60px" }} />

      <header className="sticky top-0 z-20 bg-[#060b18]/90 border-b border-white/[0.06]" style={{ backdropFilter: "blur(12px)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-7 h-7 rounded-lg bg-[#00c9a7]/10 border border-[#00c9a7]/20 flex items-center justify-center">
              <ShieldCheck size={13} className="text-[#00c9a7]" />
            </div>
            <div className="min-w-0">
              <span className="text-white font-semibold text-sm truncate">MarketAdmin</span>
              <span className="hidden sm:inline px-2 py-0.5 bg-[#00c9a7]/10 border border-[#00c9a7]/20 rounded text-[#00c9a7] text-xs font-mono ml-2">ADMIN</span>
            </div>
          </div>

          <div className="flex-1 flex justify-center px-2">
            {mainView === "dashboard" ? (
              <GlobalSearch users={users} onImpersonate={handleImpersonate} onViewUser={() => setMainView("dashboard")} />
            ) : (
              <div className="w-full" />
            )}
          </div>

          <div className="hidden sm:flex items-center gap-2">
            <button onClick={() => { console.debug("AdminDashboardLocal: problem reports button clicked"); setMainView("problem-reports"); }} className={`relative flex items-center gap-1.5 px-3 py-1.5 ${mainView === "problem-reports" ? "bg-[#0f172a]" : "bg-[#0c1428] hover:bg-[#0f1e3d]"} border border-white/[0.07] rounded-xl text-[#a0aec0] text-sm transition-all min-h-[36px]`}>
              <AlertTriangle size={14} />
              <span className="text-sm">Reports</span>
            </button>

            <button onClick={() => { setSelectedDriver(null); setSelectedDriverKyc(null); setMainView("drivers-list"); }} className={`relative flex items-center gap-1.5 px-3 py-1.5 ${mainView === "drivers-list" || mainView === "driver-detail" ? "bg-[#0f172a]" : "bg-[#0c1428] hover:bg-[#0f1e3d]"} border border-white/[0.07] rounded-xl text-[#a0aec0] text-sm transition-all min-h-[36px]`}>
              <Truck size={14} />
              <span className="text-sm">Afdani Driver</span>
              {openSOSCount > 0 ? (
                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{openSOSCount}</span>
              ) : pendingDriverKYC > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-amber-500 text-[#060b18] text-[10px] font-bold rounded-full flex items-center justify-center">{pendingDriverKYC}</span>
              )}
            </button>

            <button onClick={() => { console.debug("AdminDashboardLocal: KYC button clicked"); setSelectedRequest(null); setMainView("kyc-queue"); }} className={`relative flex items-center gap-1.5 px-3 py-1.5 ${mainView === "kyc-queue" ? "bg-[#0f172a]" : "bg-[#0c1428] hover:bg-[#0f1e3d]"} border border-white/[0.07] rounded-xl text-[#a0aec0] text-sm transition-all min-h-[36px]`}>
              <FileCheck size={14} />
              <span className="text-sm">KYC</span>
              {pendingKYC > 0 && <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-amber-500 text-[#060b18] text-[10px] font-bold rounded-full flex items-center justify-center">{pendingKYC}</span>}
            </button>

            <button onClick={() => setMainView("bookings")} className={`relative flex items-center gap-1.5 px-3 py-1.5 ${mainView === "bookings" ? "bg-[#0f172a]" : "bg-[#0c1428] hover:bg-[#0f1e3d]"} border border-white/[0.07] rounded-xl text-[#a0aec0] text-sm transition-all min-h-[36px]`}>
              <MapPin size={14} />
              <span className="text-sm">Bookings</span>
            </button>

            <button onClick={() => setMainView("disputes")} className={`relative flex items-center gap-1.5 px-3 py-1.5 ${mainView === "disputes" ? "bg-[#0f172a]" : "bg-[#0c1428] hover:bg-[#0f1e3d]"} border border-white/[0.07] rounded-xl text-[#a0aec0] text-sm transition-all min-h-[36px]`}>
              <AlertCircle size={14} />
              <span className="text-sm">Disputes</span>
            </button>

            <button onClick={() => setMainView("payments")} className={`relative flex items-center gap-1.5 px-3 py-1.5 ${mainView === "payments" ? "bg-[#0f172a]" : "bg-[#0c1428] hover:bg-[#0f1e3d]"} border border-white/[0.07] rounded-xl text-[#a0aec0] text-sm transition-all min-h-[36px]`}>
              <CreditCard size={14} />
              <span className="text-sm">Payments</span>
            </button>

            <button onClick={() => setMainView("support-tickets")} className={`relative flex items-center gap-1.5 px-3 py-1.5 ${mainView === "support-tickets" ? "bg-[#0f172a]" : "bg-[#0c1428] hover:bg-[#0f1e3d]"} border border-white/[0.07] rounded-xl text-[#a0aec0] text-sm transition-all min-h-[36px]`}>
              <MessageCircle size={14} />
              <span className="text-sm">Tickets</span>
            </button>

            <button onClick={() => setMainView("audit-log")} className={`relative flex items-center gap-1.5 px-3 py-1.5 ${mainView === "audit-log" ? "bg-[#0f172a]" : "bg-[#0c1428] hover:bg-[#0f1e3d]"} border border-white/[0.07] rounded-xl text-[#a0aec0] text-sm transition-all min-h-[36px]`}>
              <FileCheck size={14} />
              <span className="text-sm">Audit Log</span>
            </button>

            <button onClick={onLogout} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0c1428] hover:bg-red-500/10 border border-white/[0.07] rounded-xl text-[#a0aec0] hover:text-red-400 text-sm transition-all min-h-[36px]">
              <LogOut size={14} />
              <span className="text-sm">Logout</span>
            </button>
          </div>
        </div>
      </header>

      {mainView === "dashboard" && (
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="mb-5 sm:mb-6">
            <h1 className="text-white text-xl sm:text-2xl font-semibold tracking-tight">Dashboard</h1>
            <p className="text-[#6b7fa3] text-xs sm:text-sm mt-1">{new Date().toLocaleDateString()}</p>
          </div>

          <div className="overflow-x-auto pb-1 -mx-4 px-4 sm:mx-0 sm:px-0 mb-4">
            <div className="grid grid-flow-col auto-cols-[minmax(150px,1fr)] gap-3 min-w-full sm:grid-cols-6 sm:grid-flow-row sm:auto-cols-auto">
              <StatCard label="Total Users" value={totalUsers} sub="All registered accounts" icon={Users} color="#00c9a7" />
              <StatCard label="Active Sessions" value={activeSessions} sub="Currently online" icon={LayoutDashboard} color="#4f46e5" />
              <StatCard label="KYC Verified" value={kycVerified} sub="Marketplace access granted" icon={CheckCircle2} color="#00c9a7" />
              <StatCard label="Pending KYC" value={pendingKYC} sub="Awaiting review" icon={Clock} color="#d97706" />
              <StatCard label="Afdani Drivers" value={drivers.length} sub={pendingDriverKYC ? `${pendingDriverKYC} pending KYC` : "All caught up"} icon={Truck} color="#00c9a7" />
              <StatCard label="Open SOS" value={openSOSCount} sub={openSOSCount ? "Needs attention now" : "All clear"} icon={AlertTriangle} color={openSOSCount ? "#ef4444" : "#00c9a7"} />
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-[#0c1428] border border-white/[0.06] rounded-2xl overflow-hidden">
              <div className="px-4 sm:px-5 py-3.5 border-b border-white/[0.05] flex items-center justify-between">
                <h2 className="text-white font-medium text-sm">Recent Users</h2>
                <div className="text-sm text-[#00c9a7] cursor-pointer" onClick={() => setMainView("users-list")}>View all</div>
              </div>
              <div className="divide-y divide-white/[0.04]">
                {loading ? (
                  <div className="py-6 text-center text-[#6b7fa3]">Loading users…</div>
                ) : error ? (
                  <div className="py-6 text-center text-red-400">{error}</div>
                ) : users.length === 0 ? (
                  <div className="py-6 text-center text-[#6b7fa3]">No users available</div>
                ) : (
                  users.slice(0, 8).map((user) => (
                    <div key={user.id} className="flex items-center gap-3 px-4 sm:px-5 py-3 hover:bg-white/[0.02] transition-colors">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0" style={{ background: user.avatarColor + "33", border: `1px solid ${user.avatarColor}40`, color: user.avatarColor }}>{user.initials}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">{user.name}</p>
                        <p className="text-[#6b7fa3] text-xs truncate">{user.email}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <div className={`w-2 h-2 rounded-full ${user.sessionActive ? "bg-emerald-400" : "bg-[#2a3a52]"}`} />
                        <button onClick={() => handleImpersonate(user)} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0a1020] hover:bg-[#00c9a7]/10 border border-white/[0.06] rounded-lg text-[#6b7fa3] hover:text-[#00c9a7] text-xs font-medium transition-all"><ChevronRight size={12} />Access Session</button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {mainView === "users-list" && (
        <UsersListView users={users} onBack={() => setMainView("dashboard")} onImpersonate={handleImpersonate} />
      )}

      {mainView === "problem-reports" && (
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="mb-5 sm:mb-6">
            <h1 className="text-white text-xl sm:text-2xl font-semibold tracking-tight">Problem Reports</h1>
            <p className="text-[#6b7fa3] text-xs sm:text-sm mt-1">Review reported issues and mark them closed when resolved.</p>
          </div>

          <div className="grid gap-4 sm:grid-cols-3 mb-4">
            <div className="bg-[#0c1428] border border-white/[0.06] rounded-2xl p-4">
              <p className="text-sm text-[#6b7fa3]">Total reports</p>
              <p className="text-3xl font-semibold text-white mt-2">{problemReports.length}</p>
            </div>
            <div className="bg-[#0c1428] border border-white/[0.06] rounded-2xl p-4">
              <p className="text-sm text-[#6b7fa3]">Open reports</p>
              <p className="text-3xl font-semibold text-white mt-2">{problemReports.filter((r) => r.Status !== "closed").length}</p>
            </div>
            <div className="bg-[#0c1428] border border-white/[0.06] rounded-2xl p-4">
              <p className="text-sm text-[#6b7fa3]">Closed reports</p>
              <p className="text-3xl font-semibold text-white mt-2">{problemReports.filter((r) => r.Status === "closed").length}</p>
            </div>
          </div>

          <div className="bg-[#0c1428] border border-white/[0.06] rounded-2xl overflow-hidden">
            <div className="px-4 sm:px-5 py-3.5 border-b border-white/[0.05] flex items-center justify-between gap-3">
              <div>
                <h2 className="text-white font-medium text-sm">Reported Problems</h2>
                <p className="text-[#6b7fa3] text-xs mt-1">Latest reports submitted by users.</p>
              </div>
              <button
                onClick={() => { setProblemReports([]); setProblemReportsError(null); void loadProblemReports(); }}
                className="rounded-xl border border-white/[0.08] px-3 py-2 text-xs text-[#a0aec0] hover:bg-white/[0.04] transition"
              >
                Refresh
              </button>
            </div>

            <div className="p-4 space-y-3">
              {loadingProblemReports ? (
                <div className="rounded-2xl border border-border bg-background p-4 text-center text-sm text-muted-foreground">
                  Loading problem reports...
                </div>
              ) : problemReportsError ? (
                <div className="rounded-2xl border border-red-500/20 bg-[#2b1114] p-4 text-sm text-red-300">
                  {problemReportsError}
                </div>
              ) : problemReports.length === 0 ? (
                <div className="rounded-2xl border border-border bg-background p-4 text-sm text-muted-foreground">
                  No problem reports found.
                </div>
              ) : (
                problemReports.map((item) => (
                  <div key={item.ReportID ?? `${item.OpID}-${item.CreatedAt}`} className="rounded-2xl border border-white/[0.07] bg-[#09101f] p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-white">{item.Category || "General"}</p>
                        <p className="text-xs text-[#9ca3af] mt-2 whitespace-pre-line">{item.Description || "No description"}</p>
                      </div>
                      <div className="flex flex-col items-start sm:items-end gap-2">
                        <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${item.Status === "closed" ? "bg-emerald-100 text-emerald-700" : item.Status === "in_progress" ? "bg-yellow-100 text-yellow-700" : "bg-slate-100 text-slate-700"}`}>
                          {item.Status ? item.Status.replace(/_/g, " ") : "open"}
                        </span>
                        <span className="text-[11px] text-[#6b7fa3]">Submitted on {item.CreatedAt ? new Date(item.CreatedAt).toLocaleDateString() : "unknown"}</span>
                      </div>
                    </div>

                    <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-center">
                      <div className="space-y-1 text-xs text-[#6b7fa3]">
                        <div>User: {item.UserName || `OpID ${item.OpID ?? "unknown"}`}</div>
                        <div>Contact: {item.ContactNo || "Not available"}</div>
                      </div>
                      <button
                        type="button"
                        disabled={item.Status === "closed"}
                        onClick={() => void handleResolveProblemReport(item.ReportID ?? 0)}
                        className="inline-flex items-center justify-center rounded-2xl px-3 py-2 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 bg-[#0f172a] hover:bg-[#111d2e] border border-white/[0.08] text-white"
                      >
                        {item.Status === "closed" ? "Resolved" : "Mark Resolved"}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {mainView === "kyc-queue" && (
        <KYCQueueView kycRequests={kycRequests} onBack={() => setMainView("dashboard")} onSelectRequest={(r) => { setSelectedRequest(r); setMainView("kyc-detail"); void loadRequestDetail(r.id); }} />
      )}

      {mainView === "kyc-detail" && selectedRequest && (
        <KYCDetailView request={selectedRequest} onBack={() => setMainView("kyc-queue")} onReview={handleReviewRequest} />
      )}

      {mainView === "drivers-list" && (
        <>
          {loadingDrivers && drivers.length === 0 ? (
            <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-14 text-center text-[#6b7fa3] text-sm">Loading drivers…</div>
          ) : driversError ? (
            <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-14 text-center text-red-400 text-sm">{driversError}</div>
          ) : (
            <DriversListView drivers={drivers} sosAlerts={sosAlerts} onBack={() => setMainView("dashboard")} onSelectDriver={handleSelectDriver} onResolveSOS={handleResolveSOS} />
          )}
        </>
      )}

      {mainView === "driver-detail" && selectedDriver && (
        <DriverDetailView
          driver={selectedDriver}
          kyc={selectedDriverKyc}
          onBack={() => setMainView("drivers-list")}
          onReviewKyc={handleDriverKycReview}
          onToggleActive={handleToggleDriverActive}
          onAccessDriver={handleAccessDriver}
          accessingDriver={accessingDriver}
        />
      )}

      {mainView === "bookings" && <AdminBookings onBack={() => setMainView("dashboard")} />}
      {mainView === "disputes" && <AdminDisputes onBack={() => setMainView("dashboard")} />}
      {mainView === "payments" && <AdminPayments onBack={() => setMainView("dashboard")} />}
      {mainView === "support-tickets" && <AdminSupportTickets onBack={() => setMainView("dashboard")} />}
      {mainView === "audit-log" && <AdminAuditLog onBack={() => setMainView("dashboard")} />}

      {mainView !== "kyc-detail" && (
        <MobileBottomNav mainView={mainView} setMainView={setMainView} pendingKYC={pendingKYC} pendingDriverKYC={pendingDriverKYC} openSOSCount={openSOSCount} onLogout={onLogout} />
      )}
    </div>
  );
}
